// eslint-disable-next-line no-restricted-imports
import { Formatter, query, zero } from 'fp-ts-routing';

import { adt, iots } from '@code-expert/prelude';
import type { PartialProps } from '@code-expert/type-utils';
import {
  ExamId,
  GroupId,
  ProjectEntryKey,
  SnapshotId,
  SubmissionId,
  TaskId,
} from '/imports/domain';
import type { UnwrapMatch } from '/imports/modules/Routing';
import { end, formatRoute, lit, Match, type, url } from '/imports/modules/Routing';
import { MessagesSection } from '/imports/ui/pages/myCourses/messages/navigation';

const review = lit('review');
const groupId = type('groupId', GroupId);
const taskId = type('taskId', TaskId);
const submissionId = type('submissionId', SubmissionId);
const solution = lit('solution');

const queryParams = query(
  iots.exact(
    iots.partial({
      fileKey: ProjectEntryKey,
      snapshotId: SnapshotId,
      section: MessagesSection,
    }),
  ),
);

const mainRoute = url(review, groupId, taskId, queryParams, end);
const submissionRoute = url(review, groupId, taskId, submissionId, queryParams, end);
const solutionRoute = url(review, groupId, taskId, submissionId, solution, queryParams, end);

export type ReviewRouteParams = UnwrapMatch<typeof mainRoute> & {
  submissionId?: SubmissionId;
  showSolution: boolean;
};

export const reviewRoute = new Match(
  zero<ReviewRouteParams>()
    .alt(mainRoute.parser.map((p) => ({ ...p, showSolution: false })))
    .alt(submissionRoute.parser.map((p) => ({ ...p, showSolution: false })))
    .alt(solutionRoute.parser.map((p) => ({ ...p, showSolution: true }))),
  new Formatter((route, { submissionId, showSolution, groupId, snapshotId, fileKey, taskId }) =>
    submissionId != null
      ? showSolution
        ? solutionRoute.formatter.run(route, { groupId, snapshotId, taskId, fileKey, submissionId })
        : submissionRoute.formatter.run(route, {
            groupId,
            snapshotId,
            taskId,
            fileKey,
            submissionId,
          })
      : mainRoute.formatter.run(route, { groupId, snapshotId, taskId, fileKey }),
  ),
);

type ReviewNavigation = (params: PartialProps<ReviewRouteParams, 'showSolution'>) => string;

export const reviewLink: ReviewNavigation = ({ showSolution = false, ...rest }) =>
  formatRoute(reviewRoute)({ ...rest, showSolution });

// -------------------------------------------------------------------------------------------------

const reviewExam = lit('reviewExam');
const examId = type('examId', ExamId);

const examMainRoute = url(reviewExam, examId, taskId, queryParams, end);
const examSubmissionRoute = url(reviewExam, examId, taskId, submissionId, queryParams, end);
const examSolutionRoute = url(reviewExam, examId, taskId, submissionId, solution, queryParams, end);

export type ExamReviewRouteParams = UnwrapMatch<typeof examMainRoute> & {
  submissionId?: SubmissionId;
  showSolution: boolean;
};

export const examReviewRoute = new Match(
  zero<ExamReviewRouteParams>()
    .alt(examMainRoute.parser.map((p) => ({ ...p, showSolution: false })))
    .alt(examSubmissionRoute.parser.map((p) => ({ ...p, showSolution: false })))
    .alt(examSolutionRoute.parser.map((p) => ({ ...p, showSolution: true }))),
  new Formatter((route, { submissionId, showSolution, examId, fileKey, taskId, snapshotId }) =>
    submissionId != null
      ? showSolution
        ? examSolutionRoute.formatter.run(route, {
            examId,
            fileKey,
            snapshotId,
            taskId,
            submissionId,
          })
        : examSubmissionRoute.formatter.run(route, {
            examId,
            fileKey,
            snapshotId,
            taskId,
            submissionId,
          })
      : examMainRoute.formatter.run(route, { examId, fileKey, snapshotId, taskId }),
  ),
);

type ExamReviewNavigation = (params: PartialProps<ExamReviewRouteParams, 'showSolution'>) => string;

export const examReviewLink: ExamReviewNavigation = ({ showSolution = false, ...rest }) =>
  formatRoute(examReviewRoute)({ ...rest, showSolution });

// -------------------------------------------------------------------------------------------------

const foldReviewType = adt.foldFromProp('type');
export const reviewURL = (
  reviewData:
    | ({ type: 'exam' } & PartialProps<ExamReviewRouteParams, 'showSolution'>)
    | ({ type: 'exercise' } & PartialProps<ReviewRouteParams, 'showSolution'>),
): string =>
  foldReviewType(reviewData, {
    exam: examReviewLink,
    exercise: reviewLink,
  });
